import type { TodayObservance } from "@/lib/panchang-today-observance";
import { PANCHANG_COPY } from "./Panchang.copy";

export default function PanchangHeader({
  isHindi,
  heading,
  eyebrow,
  headingLevel: Heading = "h1",
  todayFormatted,
  locationName,
  answer,
  todayObservance = null,
}: {
  isHindi: boolean;
  /** The dated, city-named answer from the engine result (w3-timing-hubs). */
  answer?: string | null;
  /**
   * Overrides the generic headline so the <h1> can name the selected city.
   *
   * A generic heading repeated across every city view is a templated-content
   * signal. `ChoghadiyaHeader` takes the same prop.
   */
  heading?: string;
  /** Replaces the "Aaj Ka Panchang" kicker on a page that is not today's. */
  eyebrow?: string;
  headingLevel?: "h1" | "h2";
  todayFormatted: string;
  locationName: string;
  /**
   * Today's festival/vrat (hub only, on its own day). It replaces the date
   * line, which the H1 and the answer already state, with what the day is
   * (C23: the festival card sat ~8 phone screens down; /hi/ had none).
   */
  todayObservance?: TodayObservance | null;
}) {
  const copy = PANCHANG_COPY[isHindi ? "hi" : "en"];

  return (
    <div className="text-center mb-10">
      <p className="text-eyebrow font-semibold uppercase tracking-eyebrow mb-3 text-intent-brand">
        {eyebrow ?? copy.eyebrow}
      </p>
      <Heading className="text-display font-bold leading-tight text-ish-dark font-serif">
        {heading ?? copy.headline}
      </Heading>
      {answer ? (
        <p className="mt-3 text-body font-semibold text-text-primary" data-timing-answer>
          {answer}
        </p>
      ) : null}
      {todayObservance ? (
        <p className="mt-2" data-panchang-today-observance>
          <a
            href={todayObservance.href}
            className="inline-flex min-h-11 items-center rounded-full border border-border-interactive bg-surface-brand-subtle px-4 text-body-small font-semibold text-text-primary hover:underline"
          >
            {isHindi ? "आज: " : "Today: "}
            {todayObservance.label}
            {todayObservance.fasting ? (isHindi ? " · व्रत" : " · vrat") : ""} →
          </a>
        </p>
      ) : (
        <p className="mt-2 text-body text-ish-muted">{todayFormatted}</p>
      )}
      <p className="mt-1 text-caption text-ish-faint">
        {isHindi
          ? `तिथि, नक्षत्र, योग, करण और राहु काल — ${locationName} के लिए`
          : `Tithi, Nakshatra, Yoga, Karana & Rahu Kaal for ${locationName}`}
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 text-caption font-medium text-ish-faint">
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          {isHindi ? "सटीक वैदिक गणना" : "Accurate Vedic calculations"}
        </span>
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          {isHindi ? "प्रतिदिन अद्यतन" : "Updated Daily"}
        </span>
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          {isHindi ? "100% निःशुल्क" : "100% Free"}
        </span>
      </div>
    </div>
  );
}
